import type { NovaEvent } from "@nova/types";

export const originator = "PersonSources" as const;

export const eventTypes = {
  onEditPersonSourceCard: "onEditPersonSourceCard",
  onDismiss: "onDismiss",
  onUnlinkPersonSourceSuccess: "onUnlinkPersonSourceSuccess",
  onUnlinkPersonSourceFailure: "onUnlinkPersonSourceFailure",
  onDeletePersonSourceSuccess: "onDeletePersonSourceSuccess",
  onDeletePersonSourceFailure: "onDeletePersonSourceFailure",
  onClickEmail: "onClickEmail",
  onClickPhone: "onClickPhone",
  onClickIMAddress: "onClickIMAddress",
  onSaveContactSuccess: "onSaveContactSuccess",
  onSaveContactFailure: "onSaveContactFailure",
} as const;

interface PersonSourceEventWrapper<D, T extends string> extends NovaEvent<D> {
  originator: typeof originator;
  type: T;
  data: () => D;
}

/**
 * Define a type for the data payload of dismissDialog
 */
export type DismissPayload = {
  personID: string;
  showUpdatedPersonOnClose: boolean;
};

/**
 * Represents the data returned upon successful unlinking of a person source.
 */
type UnlinkPersonSourceSuccessEventData = {
  /**
   * A message indicating the success of the person source unlinking.
   * This message can be shown to the user to confirm that the action was successful.
   */
  successMessage: string;

  /**
   * The unique identifiers of the persons whose sources were unlinked.
   * This can be used to update the UI to reflect the changes made to the person sources.
   * If the person sources are successfully unlinked, the host app can update the UI
   * to reflect the changes made to the relevant person(s).
   */
  personIds: string[];

  /**
   * A flag indicating whether person sources can be dismissed.
   * If the person sources are successfully unlinked and `personIds` is empty, this
   * will be set to `true` to allow the host app to close or dismiss any relevant
   * UI related to the person sources, since there will be no further data left
   * for those contact(s).
   */
  canDismiss: boolean;

  /**
   * We need to pass the directoryObjectId to the host app to allow the host app
   * to update the LPC or other underlying UI surfaces.
   */
  directoryObjectId: string | null | undefined;
};

/**
 * Represents the data returned upon successful deletion of a person source.
 */
type DeletePersonSourceSuccessEventData = {
  /**
   * A message indicating the success of the person source deletion.
   * This message can be shown to the user to confirm that the action was successful.
   */
  successMessage: string;

  /**
   * The unique identifier of the person whose source was deleted.
   * This can be null after the deletion, as the contact might no longer exist
   * after the mutation. If the person is deleted, there will be no remaining data
   * associated with this person.
   */
  personId: string | null;

  /**
   * A flag indicating whether person sources can be dismissed.
   * If the person source is successfully deleted and `personId` is null, this
   * will be set to `true` to allow the host app to close or dismiss any relevant
   * UI related to the person sources, since there will be no further data left
   * for that contact.
   */
  canDismiss: boolean;
};

// TODO check if we would need any PersonID for the failure events.
type PersonSourceMutationFailureEventData = {
  errorMessage: string;
  failureCode: string;
};

type SaveContactSuccessEventData = {
  contactId: string;
  successMessage: string;
};

type SaveContactFailureEventData = {
  errorCode: string;
  errorMessage: string;
};

/**
 * Define a type for the data payload for compose email request
 */
type ComposeEmailRequestData = {
  /**
   * The URL to open the email client or compose window.
   */
  emailUrl: string;

  /**
   * The email address of the person source
   */
  email: string;
};

export type PersonSourceEvent =
  | PersonSourceEventWrapper<string, typeof eventTypes.onEditPersonSourceCard>
  | PersonSourceEventWrapper<DismissPayload, typeof eventTypes.onDismiss>
  | PersonSourceEventWrapper<
      ComposeEmailRequestData,
      typeof eventTypes.onClickEmail
    >
  | PersonSourceEventWrapper<string, typeof eventTypes.onClickPhone>
  | PersonSourceEventWrapper<string, typeof eventTypes.onClickIMAddress>
  | PersonSourceEventWrapper<
      UnlinkPersonSourceSuccessEventData,
      typeof eventTypes.onUnlinkPersonSourceSuccess
    >
  | PersonSourceEventWrapper<
      PersonSourceMutationFailureEventData,
      typeof eventTypes.onUnlinkPersonSourceFailure
    >
  | PersonSourceEventWrapper<
      DeletePersonSourceSuccessEventData,
      typeof eventTypes.onDeletePersonSourceSuccess
    >
  | PersonSourceEventWrapper<
      PersonSourceMutationFailureEventData,
      typeof eventTypes.onDeletePersonSourceFailure
    >
  | PersonSourceEventWrapper<
      SaveContactSuccessEventData,
      typeof eventTypes.onSaveContactSuccess
    >
  | PersonSourceEventWrapper<
      SaveContactFailureEventData,
      typeof eventTypes.onSaveContactFailure
    >;
